import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError, mediaUrl } from "@/lib/api";
import { uploadFile } from "@/lib/upload";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ChevronDown, ChevronUp, ImageUp, RotateCcw, Save } from "lucide-react";

/**
 * Admin → Site Content: every headline, paragraph and hero image on the public
 * site, grouped by page. Editing here is the only thing needed to change wording
 * or swap an image — no code change, no rebuild.
 */
export const ContentTab = () => {
  const [groups, setGroups] = useState([]);
  const [draft, setDraft] = useState({});
  const [open, setOpen] = useState({});
  const [busy, setBusy] = useState("");

  const load = async () => {
    const { data } = await api.get("/admin/content");
    setGroups(data.groups);
    setDraft(Object.fromEntries(data.groups.flatMap((g) => g.fields.map((f) => [f.key, f.value]))));
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { if (groups.length && !Object.keys(open).length) setOpen({ [groups[0].key]: true }); }, [groups]); // eslint-disable-line

  const set = (key, value) => setDraft((d) => ({ ...d, [key]: value }));

  const saveGroup = async (group) => {
    const values = Object.fromEntries(group.fields.map((f) => [f.key, draft[f.key] ?? f.value]));
    setBusy(group.key);
    try {
      await api.put("/admin/content", { values });
      toast.success(`${group.label} saved — the site is updated`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const resetField = async (field) => {
    try {
      await api.post("/admin/content/reset", { keys: [field.key] });
      toast.success("Restored the original text");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const onImage = async (field, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(field.key);
    try {
      const { url } = await uploadFile(file, "branding", { maxWidth: 1800 });
      set(field.key, url);
      toast.success("Image uploaded — press Save to publish");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  return (
    <div className="space-y-4" data-testid="content-tab">
      <p className="text-sm text-muted-foreground">
        Change any wording or image on the public site here. &quot;Reset&quot; puts the original text back.
      </p>
      {groups.map((group) => (
        <Card key={group.key} className="border-border" data-testid={`content-group-${group.key}`}>
          <CardContent className="p-5 lg:p-6">
            <button
              className="flex w-full items-center justify-between gap-3"
              onClick={() => setOpen((o) => ({ ...o, [group.key]: !o[group.key] }))}
              data-testid={`content-toggle-${group.key}`}
            >
              <span className="font-display text-2xl flex items-center gap-2">{group.label}</span>
              <span className="flex items-center gap-2 text-xs text-muted-foreground">
                {group.fields.filter((f) => f.edited).length > 0 && (
                  <Badge variant="outline" className="border-primary/40 text-primary bg-primary/10 text-[10px]">
                    {group.fields.filter((f) => f.edited).length} edited
                  </Badge>
                )}
                {group.fields.length} fields
                {open[group.key] ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
              </span>
            </button>

            {open[group.key] && (
              <div className="mt-6 space-y-5">
                {group.fields.map((field) => (
                  <div key={field.key} className="grid lg:grid-cols-[220px_1fr] gap-3 lg:gap-6 items-start">
                    <div>
                      <Label className="overline">{field.label}</Label>
                      <div className="text-[10px] text-muted-foreground font-mono mt-1">{field.key}</div>
                      {field.edited && (
                        <button
                          onClick={() => resetField(field)}
                          className="text-[11px] text-secondary hover:underline inline-flex items-center gap-1 mt-1"
                          data-testid={`content-reset-${field.key}`}
                        >
                          <RotateCcw size={10} /> Reset
                        </button>
                      )}
                    </div>
                    {field.type === "image" ? (
                      <div className="flex flex-wrap items-center gap-4">
                        {draft[field.key] ? (
                          <img
                            src={mediaUrl(draft[field.key])}
                            alt={field.label}
                            className="w-32 h-20 object-cover rounded-lg border border-border"
                            data-testid={`content-image-${field.key}`}
                          />
                        ) : (
                          <div className="w-32 h-20 rounded-lg border border-dashed border-border" />
                        )}
                        <label className="btn-pill border border-border px-4 py-2 text-xs cursor-pointer hover:border-primary/40 inline-flex items-center gap-1.5">
                          <ImageUp size={13} /> {busy === field.key ? "Uploading…" : "Replace image"}
                          <input type="file" accept="image/*" className="hidden" onChange={(e) => onImage(field, e)} data-testid={`content-upload-${field.key}`} />
                        </label>
                        <Input
                          value={draft[field.key] || ""}
                          onChange={(e) => set(field.key, e.target.value)}
                          placeholder="…or paste an image URL"
                          className="bg-transparent flex-1 min-w-[200px]"
                          data-testid={`content-input-${field.key}`}
                        />
                      </div>
                    ) : field.type === "textarea" ? (
                      <Textarea
                        rows={field.key.startsWith("legal.") ? 16 : 3}
                        value={draft[field.key] || ""}
                        onChange={(e) => set(field.key, e.target.value)}
                        className="bg-transparent"
                        data-testid={`content-input-${field.key}`}
                      />
                    ) : (
                      <Input
                        value={draft[field.key] || ""}
                        onChange={(e) => set(field.key, e.target.value)}
                        className="bg-transparent"
                        data-testid={`content-input-${field.key}`}
                      />
                    )}
                  </div>
                ))}
                <Button
                  onClick={() => saveGroup(group)}
                  disabled={busy === group.key}
                  className="btn-pill bg-primary text-primary-foreground h-10"
                  data-testid={`content-save-${group.key}`}
                >
                  <Save size={14} className="mr-1.5" /> {busy === group.key ? "Saving…" : `Save ${group.label}`}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default ContentTab;
